import React, { useEffect, useState } from "react";
import { useI18n } from "../../i18n";
import api from "../../api";

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_TYPES = ["hard_training", "light_training", "double_session", "rest", "match"];
const MEALS = ["lunch", "dinner", "snack2"];

const Cravings = ({ profile }) => {
  const { t, lang } = useI18n();
  const isLifestyle = profile?.profile_type === "lifestyle" || profile?.sport === "lifestyle";
  const [cravings, setCravings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dayKey, setDayKey] = useState("saturday");
  const [dayType, setDayType] = useState(isLifestyle ? "standard" : "light_training");
  const [meal, setMeal] = useState("dinner");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/catalog/cravings").then((r) => setCravings(r.data.cravings));
  }, []);

  const apply = async () => {
    setError(null); setResult(null);
    if (!selected) return;
    setLoading(true);
    try {
      const r = await api.post("/plan/craving", {
        profile, craving_key: selected, meal_id: meal, day_type: dayType, day_key: dayKey,
      });
      if (!r.data.ok) {
        setError(r.data[`error_${lang}`] || r.data.error_en || r.data.error);
      } else {
        setResult(r.data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <h3 className="font-heading text-2xl mb-2">{t.cravings.title}</h3>
        <p className="text-white/60 mb-5">{t.cravings.subtitle}</p>

        <div className="grid grid-cols-2 gap-3">
          {cravings.map((c) => (
            <button
              key={c.key}
              data-testid={`craving-${c.key}`}
              onClick={() => setSelected(c.key)}
              className="text-left p-4"
              style={{
                background: selected === c.key ? "#181111" : "#141414",
                border: `1px solid ${selected === c.key ? "#FF3B30" : "#27272A"}`,
                cursor: "pointer",
              }}
            >
              <div className="font-heading text-lg leading-tight">{c[`label_${lang}`] || c.label_en}</div>
              <div className="font-mono text-xs text-white/60 mt-2">
                {c.kcal} kcal · P{c.protein} · C{c.carbs} · F{c.fat}
              </div>
              <div className="mt-2 font-mono text-[10px] tracking-widest text-[#FF3B30]">
                {t.cravings.max_week}: {c.max_per_week}
              </div>
            </button>
          ))}
        </div>

        <div className="mt-6 sne-card">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="min-w-0">
              <label className="sne-label">{t.cravings.day_key}</label>
              <select className="sne-select w-full" value={dayKey} onChange={(e) => setDayKey(e.target.value)} data-testid="craving-day">
                {DAY_KEYS.map((d) => <option key={d} value={d}>{t.plan.days[d]}</option>)}
              </select>
            </div>
            {!isLifestyle && (
              <div className="min-w-0">
                <label className="sne-label">{t.cravings.day_type}</label>
                <select className="sne-select w-full" value={dayType} onChange={(e) => setDayType(e.target.value)} data-testid="craving-daytype">
                  {DAY_TYPES.map((d) => <option key={d} value={d}>{t.plan.day_types_short?.[d] || t.plan.day_types[d]}</option>)}
                </select>
              </div>
            )}
            <div className="min-w-0">
              <label className="sne-label">{t.cravings.meal}</label>
              <select className="sne-select w-full" value={meal} onChange={(e) => setMeal(e.target.value)} data-testid="craving-meal">
                <option value="lunch">{lang === "ro" ? "Prânz" : "Lunch"}</option>
                <option value="dinner">{lang === "ro" ? "Cină" : "Dinner"}</option>
                <option value="snack2">{lang === "ro" ? "Gustare" : "Snack"}</option>
              </select>
            </div>
          </div>
          <button onClick={apply} disabled={!selected || loading} className="btn-primary w-full mt-4" data-testid="craving-apply">
            {loading ? t.common.loading : t.cravings.apply}
          </button>
        </div>
      </div>

      <div>
        {error && (
          <div className="sne-card" style={{ borderColor: "#FF3B30" }}>
            <div className="font-mono text-xs tracking-widest text-[#FF3B30] mb-2">⚠ {t.cravings.not_allowed}</div>
            <div className="text-sm text-white" data-testid="craving-error">{error}</div>
          </div>
        )}
        {result && (
          <div className="sne-card animate-fade-up" data-testid="craving-result">
            <div className="font-mono text-xs tracking-widest text-[#FF3B30] mb-2">REBUILT DAY</div>
            <div className="flex items-baseline justify-between mb-4">
              <span className="stat-num text-4xl">{Math.round(result.plan.totals.kcal)}</span>
              <span className="font-mono text-xs text-white/50">kcal</span>
            </div>
            {result.plan.craving_warning && (
              <div className="mb-4 p-3" style={{ background: "#2A1A0F", border: "1px solid #F5A623" }}>
                <div className="font-mono text-[10px] tracking-widest text-amber-400 mb-1">⚠ {t.cravings.warning}</div>
                <div className="text-sm text-white/90">{result.plan.craving_warning[lang]}</div>
              </div>
            )}
            <div className="space-y-2">
              {result.plan.meals.map((m) => (
                <div key={m.id} className="pb-2" style={{ borderBottom: "1px solid #1F1F1F" }}>
                  <div className="flex justify-between">
                    <span className="font-heading text-lg">{m[`title_${lang}`] || m.title_en}</span>
                    <span className="font-mono text-xs text-white/60">{Math.round(m.totals.kcal)} kcal</span>
                  </div>
                  {m.is_craving && <span className="font-mono text-[10px] tracking-widest text-[#FF3B30]">★ CRAVING</span>}
                  <div className="font-mono text-xs text-white/50 mt-1">
                    {m.foods.map((f) => `${f[`name_${lang}`] || f.name_en} ${Math.round(f.grams)}g`).join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cravings;
